import type React from 'react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import * as styles from './styles';

import { type AccessibleFleet, Location, locationName, UserPermission } from '@cobliteam/common-types';

import { Analytics } from '@cobliteam/analytics';

import { useUser } from '@cobliteam/hooks';

import { CaptionDefault, ChevronIcon, ImagesIcon, MenuItem, MenuItemType, Popper } from '@cobliteam/base-components';
import { PermissionsGate } from '@cobliteam/base-components/src/PermissionsGate';

import Icon from '@cobliteam/ui/icons';

import { SidebarFleet } from '../SidebarFleet/SidebarFleet';
import SidebarOnboarding from '../SidebarOnboarding';

export interface SidebarUserProps {
  sidebarOpen: boolean;
  currentLocation: Location;
  changeUserFleet: (fleet: AccessibleFleet) => void;
}

export const SidebarUser = ({ sidebarOpen, currentLocation, changeUserFleet }: SidebarUserProps) => {
  const { useGetUserData, getSession } = useUser();

  const { data: userData } = useGetUserData();
  const session = getSession();

  const [popperHover, setPopperHover] = useState(false);
  const [transitionGuard, setTransitionGuard] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [showPlaceholderLogo, setShowPlaceholderLogo] = useState(false);

  const [isSettingsMenuHover, setIsSettingsMenuHover] = useState(false);
  const [isCrmDevolutionMenuHover, setIsCrmDevolutionMenuHover] = useState(false);

  const [isCustomerSectionMenuHover, setIsCustomerSectionMenuHover] = useState(false);

  const [isActive, setIsActive] = useState(currentLocation === Location.CONFIG);

  const isConfigCurrentPage = currentLocation === Location.CONFIG;
  const isCrmDevolutionCurrentPage = currentLocation === Location.CRM_SCHEDULE;

  const userFleetName = session.fleet.name;

  const accessibleFleets: AccessibleFleet[] = userData?.userFleets || [];
  const currentFleetId = session.fleet.id;

  const userEmail = session.user.email;

  const otherUserFleets = accessibleFleets.filter((accessibleFleet) => accessibleFleet.id !== currentFleetId);
  const hasOtherUserFleets = !!otherUserFleets.length;

  const handleClick = (item: Location | string) => () => {
    Analytics.track('Sidebar Item Clicked', { page: item });

    if (item === Location.CONFIG) {
      setIsActive(true);
    }
  };

  const handleLogoError = () => setShowPlaceholderLogo(true);

  const getLogo = () =>
    showPlaceholderLogo ? (
      <ImagesIcon className={styles.imagePlaceholder} />
    ) : (
      <img alt='fleetLogo' src={`https://assets.cobli.co/fleets/${currentFleetId}/logo.png`} onError={handleLogoError} />
    );

  const handleWrapperClickOrEnter = () => {
    setIsActive(true);
  };

  const handleWrapperMouseLeave = () => {
    setIsActive(false);
  };

  const handleMouseClickOrEnter = (event: React.MouseEvent<HTMLElement>) => {
    Analytics.track('Sidebar Item Clicked', { page: 'account-configs' });
    setAnchorEl(event.currentTarget);
    setTransitionGuard(!transitionGuard);
  };

  const handleMouseLeave = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setTransitionGuard(false);
  };

  const handlePopperMouseLeave = () => {
    setPopperHover(false);
    setTransitionGuard(false);
  };

  const renderSidebarFleet = () => {
    const showPopper = popperHover || transitionGuard;
    const isConfigOptionActive = isConfigCurrentPage || isSettingsMenuHover;
    const isCrmDevolutionOptionActive = isCrmDevolutionCurrentPage || isCrmDevolutionMenuHover;

    return (
      <Popper
        open={showPopper}
        arrow={false}
        placement='right-start'
        paperClassName={styles.popper(hasOtherUserFleets)}
        handleMouseEnter={() => {
          setPopperHover(true);
          setTransitionGuard(true);
        }}
        handleMouseLeave={handlePopperMouseLeave}
        anchorEl={anchorEl}
      >
        <SidebarOnboarding isSidebarUser sidebarOpen={false} />
        <Link
          to={Location.CONFIG}
          className={styles.sidebarItem(isActive)}
          onClick={handleClick(Location.CONFIG)}
          onMouseEnter={() => setIsSettingsMenuHover(true)}
          onMouseLeave={() => setIsSettingsMenuHover(false)}
        >
          <div className={styles.iconWrapper(isConfigOptionActive)}>
            <Icon name='settings' />
          </div>
          <MenuItem type={MenuItemType.LIGHT}>{locationName[Location.CONFIG]}</MenuItem>
        </Link>
        <a
          className={styles.sidebarItem(isActive)}
          href='https://suporte.cobli.co/tickets-view'
          target='_blank'
          rel='noreferrer'
          onMouseEnter={() => setIsCustomerSectionMenuHover(true)}
          onMouseLeave={() => setIsCustomerSectionMenuHover(false)}
          onClick={handleClick('customer-section')}
        >
          <div className={styles.iconWrapper(isCustomerSectionMenuHover)}>
            <Icon name='square-arrow-out-up-right' />
          </div>
          <MenuItem type={MenuItemType.LIGHT} active={isCustomerSectionMenuHover}>
            Área do cliente
          </MenuItem>
        </a>
        <PermissionsGate anyOfPermissions={UserPermission.CREATE_UPDATE_DELETE_VEHICLE}>
          <Link
            to={Location.CRM_SCHEDULE}
            className={styles.sidebarItem(isActive)}
            onClick={handleClick(Location.CRM_SCHEDULE)}
            onMouseEnter={() => setIsCrmDevolutionMenuHover(true)}
            onMouseLeave={() => setIsCrmDevolutionMenuHover(false)}
          >
            <div className={styles.iconWrapper(isCrmDevolutionOptionActive)}>
              <Icon name='archive' />
            </div>
            <MenuItem type={MenuItemType.LIGHT} active={isCrmDevolutionOptionActive}>
              Pedidos
            </MenuItem>
          </Link>
        </PermissionsGate>
        {hasOtherUserFleets && <SidebarFleet accessibleFleets={accessibleFleets} changeUserFleet={changeUserFleet} currentFleetId={currentFleetId} />}
      </Popper>
    );
  };

  const isSidebarLogoActive = isActive || isConfigCurrentPage;

  return (
    <>
      <div className={styles.wrapper} onMouseEnter={handleWrapperClickOrEnter} onMouseLeave={handleWrapperMouseLeave}>
        <div className={styles.item(isActive)} onClick={sidebarOpen ? handleMouseClickOrEnter : undefined} onMouseEnter={handleMouseClickOrEnter} onMouseLeave={handleMouseLeave}>
          <div id='sidebarWrapper' className={styles.itemContainer}>
            <div className={styles.logo(isSidebarLogoActive, sidebarOpen)}>
              {getLogo()}
              <Icon name='settings' className={styles.settingsIcon} />
            </div>
            {sidebarOpen && (
              <>
                <div data-cy='SidebarFleet' className={styles.settings}>
                  <MenuItem type={MenuItemType.LIGHT} active={isActive}>
                    <CaptionDefault ellipsis>{userFleetName}</CaptionDefault>
                  </MenuItem>
                  <CaptionDefault ellipsis>{userEmail}</CaptionDefault>
                </div>
                <ChevronIcon className={styles.chevronIcon} />
              </>
            )}
          </div>
        </div>
        {renderSidebarFleet()}
      </div>
    </>
  );
};
